// src/hooks/usePricingAccess.js
import { useEffect, useState } from 'react';
import { useAuth } from '../components/login/AuthProvider';

const API_URL = process.env.REACT_APP_API_URL || 'https://hms.backend.hiragroupcloud.com';

// Roles considered "Admin" (case-insensitive). Add more here if needed.
const ADMIN_ROLES = ['admin', 'superadmin', 'super-admin', 'super_admin'];

/**
 * Check whether a given user object represents an admin.
 * Looks at multiple possible fields to be robust across auth shapes.
 */
const isAdminFromUserObject = (user) => {
  if (!user) return false;

  const role =
    user.role ||
    user.userRole ||
    user?.data?.role ||
    user?.data?.userRole ||
    user?.user?.role ||
    user?.user?.userRole;

  if (!role) return false;

  return ADMIN_ROLES.includes(String(role).trim().toLowerCase());
};

/**
 * Fallback: check localStorage keys directly in case useAuth()
 * hasn't populated the user yet (or the shape is different).
 * If an Admin role is found anywhere in localStorage, return true.
 */
const isAdminFromLocalStorage = () => {
  try {
    const possibleKeys = [
      'user',
      'userData',
      'auth',
      'currentUser',
      'labUser',
      'labUserData',
    ];

    for (const key of possibleKeys) {
      const stored = localStorage.getItem(key);
      if (!stored) continue;

      try {
        const parsed = JSON.parse(stored);

        const role =
          parsed?.role ||
          parsed?.userRole ||
          parsed?.data?.role ||
          parsed?.data?.userRole ||
          parsed?.user?.role ||
          parsed?.user?.userRole;

        if (role && ADMIN_ROLES.includes(String(role).trim().toLowerCase())) {
          return true;
        }
      } catch (_) {
        // Not JSON — check raw string (e.g. role stored as a plain value)
        const rawRole = String(stored).trim().toLowerCase();
        if (ADMIN_ROLES.includes(rawRole)) {
          return true;
        }
      }
    }

    return false;
  } catch (_) {
    return false;
  }
};

export default function usePricingAccess() {
  const { user } = useAuth();
  const [hasPricingAccess, setHasPricingAccess] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const userId =
      user?.userId ||
      user?.id ||
      user?.data?.userId ||
      user?.data?.id ||
      user?.user?.userId ||
      user?.user?.id;

    // ✅ Admins always have pricing access — no API call needed.
    //    Checked both from the AuthProvider user AND directly from localStorage,
    //    so it works even if useAuth hasn't hydrated yet.
    if (isAdminFromUserObject(user) || isAdminFromLocalStorage()) {
      setHasPricingAccess(true);
      setLoading(false);
      return;
    }

    // No logged-in user → no pricing access
    if (!userId) {
      setHasPricingAccess(false);
      setLoading(false);
      return;
    }

    let mounted = true;

    fetch(`${API_URL}/api/page/user/${userId}/pricing-access`)
      .then((r) => r.json())
      .then((data) => {
        if (mounted) {
          setHasPricingAccess(!!data.hasPricingAccess);
        }
      })
      .catch(() => {
        if (mounted) setHasPricingAccess(false);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [user]);

  return { hasPricingAccess, loading };
}